import { useEffect, useState } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError, fetchConfig } from "../lib/api";
import { StatusPill } from "../components/ui-bits";
import { LeafTile } from "../components/Logo";
import AdminNotifications from "../components/AdminNotifications";
import AdminPartners from "../components/AdminPartners";
import ConfigEditor from "../components/ConfigEditor";
import { prettySlot } from "../components/AggregatorBookingModal";

const TABS = [
  { id: "bookings", label: "Bookings" },
  { id: "partners", label: "Partners" },
  { id: "aggregator", label: "CARZA Nearby Bookings" },
  { id: "b2b", label: "B2B Leads" },
  { id: "partner", label: "Partner Applications" },
  { id: "investor", label: "Investor Requests" },
  { id: "notifications", label: "Notifications" },
  { id: "config", label: "Site Config" },
];
const STATUSES = ["new", "contacted", "converted", "closed"];
const BOOKING_STATUSES = ["PAYMENT_PENDING", "CONFIRMED", "ASSIGNED", "PARTNER_ON_THE_WAY", "SERVICE_IN_PROGRESS", "COMPLETED", "CANCELLED"];
const AGG_STATUSES = ["Booking Requested", "Booking Confirmed", "Reschedule Requested", "Service In Progress", "Service Completed", "Cancelled by Customer", "Cancelled by Centre", "No-Show"];

function useAuth() {
  const [token, setToken] = useState(() => localStorage.getItem("carza_admin_token"));
  const [email, setEmail] = useState(() => localStorage.getItem("carza_admin_email") || "");
  const logout = () => {
    localStorage.removeItem("carza_admin_token");
    localStorage.removeItem("carza_admin_email");
    setToken(null);
    setEmail("");
  };
  return { token, setToken, email, setEmail, logout };
}

function Login({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.login(email, password);
      onAuth(res.token, res.email);
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiError(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="glass flex w-full max-w-sm flex-col gap-4 rounded-3xl p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3">
          <LeafTile size={34} />
          <div>
            <p className="font-logo text-sm tracking-[0.18em] text-paper">CARZA</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-mist-2">Admin Console</p>
          </div>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Email</span>
          <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@carza.in" autoComplete="username" data-testid="admin-email-input" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Password</span>
          <input className="field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" autoComplete="current-password" data-testid="admin-password-input" />
        </label>
        {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} className="btn btn-primary w-full py-3.5 disabled:opacity-50" data-testid="admin-login-submit">
          {loading ? "Signing in…" : <>Sign In <ShieldCheck size={15} /></>}
        </button>
      </form>
    </main>
  );
}

const IGNORED_KEYS = new Set(["id", "kind", "company_website", "status"]);
const LABELS = { booking_id: "Booking ID", time_slot: "Time slot", full_name: "Name", fund_org: "Fund / Org", business_type: "Business type", service_area: "Service area" };

function PartnerOps({ item, token, onDone }) {
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const isAssign = item.booking_status === "CONFIRMED";

  useEffect(() => {
    if (!isAssign) return;
    adminApi.partners(token)
      .then((ps) => setPartners(ps.filter((p) => p.availability === "free")))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [isAssign, item.booking_status]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!["CONFIRMED", "ASSIGNED", "PARTNER_ON_THE_WAY", "SERVICE_IN_PROGRESS"].includes(item.booking_status)) return null;

  return (
    <div className="mt-3 rounded-xl border border-carza/25 bg-carza/[0.05] p-3" data-testid={`partner-ops-${item.id}`}>
      {isAssign ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="field flex-1" data-testid={`assign-partner-select-${item.id}`}>
            <option value="">{loaded && partners.length === 0 ? "No free partners — free one from the Partners tab" : "Choose a free partner…"}</option>
            {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.name} · +91 {p.phone}</option>)}
          </select>
          <button disabled={!partnerId || busy} onClick={() => run(() => adminApi.assignPartner(item.booking_id, partnerId, token), "Partner assigned — they'll see it on their phone")} className="btn btn-primary px-4 py-2.5 text-[12px] disabled:opacity-40" data-testid={`assign-partner-btn-${item.id}`}>
            {busy ? "Assigning…" : "Assign"}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-carza-lite" data-testid={`assigned-partner-${item.id}`}>
            On job: {item.assigned_partner_name} · +91 {item.assigned_partner_phone}
          </span>
          {["PARTNER_ON_THE_WAY", "SERVICE_IN_PROGRESS", "COMPLETED"].filter((s) => s !== item.booking_status).map((s) => (
            <button key={s} disabled={busy} onClick={() => run(() => adminApi.jobStatus(item.booking_id, s, token), `Marked ${s.replaceAll("_", " ").toLowerCase()}`)} className="btn btn-outline px-3 py-1.5 text-[11px] disabled:opacity-40" data-testid={`job-status-${s.toLowerCase()}-${item.id}`}>
              {s === "PARTNER_ON_THE_WAY" ? "On the way" : s === "SERVICE_IN_PROGRESS" ? "In progress" : "Completed"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ item, kind, token, onUpdate }) {
  const [status, setStatus] = useState(item.booking_status || item.status || "new");
  const [saving, setSaving] = useState(false);
  const change = async (s) => {
    setSaving(true);
    setStatus(s);
    try {
      await adminApi.setStatus(kind, item.id, s, token);
      toast.success(`Marked ${s.toLowerCase()}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const entries = Object.entries(item).filter(([k]) => !IGNORED_KEYS.has(k) && k !== "created_at");
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`admin-item-${kind}-${item.id}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-mist-2">
          {new Date(item.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </span>
        <select value={status} onChange={(e) => change(e.target.value)} disabled={saving} className="rounded-lg border border-white/10 bg-[#180b30] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-paper" data-testid={`admin-status-select-${kind}-${item.id}`}>
          {(kind === "bookings" ? BOOKING_STATUSES : kind === "aggregator" ? AGG_STATUSES : STATUSES).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {entries.map(([k, v]) => (
          <div key={k} className="flex gap-2 text-[13px]">
            <span className="shrink-0 text-mist-2">{LABELS[k] || k.replace(/_/g, " ")}:</span>
            <span className="break-words text-paper">{kind === "aggregator" && k === "time_slot" && /^\d{2}:\d{2}$/.test(String(v ?? "")) ? prettySlot(v) : String(v ?? "")}</span>
          </div>
        ))}
      </div>
      <div className="mt-3"><StatusPill status={status} /></div>
      {kind === "bookings" && <PartnerOps item={item} token={token} onDone={onUpdate} />}
    </div>
  );
}

function RazorpaySetup({ cfg }) {
  if (!cfg || cfg.configured) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] p-5" data-testid="razorpay-setup-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">Razorpay Setup Required — payments are not live yet</p>
      <ol className="mt-3 flex list-decimal flex-col gap-1.5 pl-5 text-[12.5px] leading-relaxed text-mist">
        <li>Create a Razorpay account (Test Mode) at dashboard.razorpay.com</li>
        <li>Copy <span className="text-paper">RAZORPAY_KEY_ID</span> and <span className="text-paper">RAZORPAY_KEY_SECRET</span> from Settings → API Keys</li>
        <li>Add them to <span className="text-paper">/app/backend/.env</span> along with <span className="text-paper">RAZORPAY_WEBHOOK_SECRET</span> and <span className="text-paper">PAYMENT_MODE=test</span></li>
        <li>Restart the backend — the booking flow will start accepting real (test-mode) payments</li>
        <li>In the Razorpay Dashboard, register a webhook on <span className="text-paper">/api/payments/webhook</span> for payment.captured and payment.failed events</li>
        <li>Go live later by switching to live API keys and <span className="text-paper">PAYMENT_MODE=live</span></li>
      </ol>
    </div>
  );
}

function Dashboard({ token, email, logout }) {
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payCfg, setPayCfg] = useState(null);
  const [payFilter, setPayFilter] = useState("All");
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await adminApi.exportExcel(token);
      const url = URL.createObjectURL(new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `carza-data-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const loadTab = async (t) => {
    setLoading(true);
    try {
      if (t === "config") {
        setConfig(await fetchConfig());
      } else if (t !== "notifications") {
        setItems(await adminApi.list(t, token));
      }
      setStats((await adminApi.stats(token)).stats);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/config`).then((r) => r.json()).then(setPayCfg).catch(() => {});
  }, []);
  useEffect(() => { loadTab(tab); }, [tab]);

  const filteredItems = tab === "bookings" && payFilter !== "All"
    ? items.filter((i) => (payFilter === "Completed" ? i.booking_status === "COMPLETED" : (i.payment_status || "").toLowerCase() === payFilter.toLowerCase()))
    : items;

  return (
    <main className="min-h-screen bg-ink pb-24" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b">
        <div className="container-x flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <LeafTile size={30} />
            <span className="font-logo text-[13px] tracking-[0.18em] text-paper">CARZA <span className="text-carza">Admin</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-[11px] text-mist-2 sm:block">{email}</span>
            <button onClick={exportExcel} disabled={exporting} className="btn btn-primary px-4 py-2 text-[12px] disabled:opacity-50" data-testid="admin-export-excel-btn">
              {exporting ? "Preparing…" : "Download Excel"}
            </button>
            <button onClick={logout} className="btn btn-outline px-4 py-2 text-[12px]" data-testid="admin-logout-btn"><LogOut size={13} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {TABS.filter((t) => !["config", "notifications", "partners"].includes(t.id)).map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`card-surface rounded-2xl p-4 text-left transition-colors ${tab === t.id ? "!border-carza/50" : ""}`} data-testid={`admin-stat-${t.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">{t.label}</p>
              <p className="mt-2 font-display text-3xl font-bold text-paper">{stats ? stats[t.id]?.total ?? "—" : "—"}</p>
              <p className="mt-1 font-mono text-[10px] text-carza-lite">{stats ? `${stats[t.id]?.new ?? 0} new` : ""}</p>
            </button>
          ))}
        </div>

        <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`shrink-0 rounded-full border px-4 py-2 text-[13px] font-medium transition-colors ${tab === t.id ? "border-carza/50 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:text-paper"}`} data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-mist-2">Loading…</div>
          ) : tab === "notifications" ? (
            <AdminNotifications token={token} />
          ) : tab === "partners" ? (
            <AdminPartners token={token} />
          ) : tab === "config" ? (
            <><RazorpaySetup cfg={payCfg} />{config ? <ConfigEditor config={config} token={token} /> : <p className="text-sm text-mist-2">Config unavailable.</p>}</>
          ) : tab === "bookings" ? (
            <>
              <RazorpaySetup cfg={payCfg} />
              <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto">
                {["All", "Paid", "Pending", "Failed", "Refunded", "Completed"].map((f) => (
                  <button key={f} onClick={() => setPayFilter(f)} className={`shrink-0 rounded-full border px-4 py-1.5 text-[12px] font-semibold transition-colors ${payFilter === f ? "border-carza/50 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:text-paper"}`} data-testid={`admin-pay-filter-${f.toLowerCase()}`}>
                    {f}
                  </button>
                ))}
              </div>
              {filteredItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-mist-2" data-testid="admin-empty-state">No {payFilter.toLowerCase()} bookings yet.</div>
              ) : (
                <div className="flex flex-col gap-4">
                  {filteredItems.map((item) => <SubmissionCard key={item.id} item={item} kind={tab} token={token} onUpdate={() => loadTab(tab)} />)}
                </div>
              )}
            </>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-mist-2" data-testid="admin-empty-state">No {tab} submissions yet.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((item) => <SubmissionCard key={item.id} item={item} kind={tab} token={token} onUpdate={() => loadTab(tab)} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default function AdminPage() {
  const { token, email, setToken, setEmail, logout } = useAuth();

  useEffect(() => {
    if (token) adminApi.me(token).catch(() => logout());
  }, []);

  if (!token) return <Login onAuth={(t, e) => { localStorage.setItem("carza_admin_token", t); localStorage.setItem("carza_admin_email", e); setToken(t); setEmail(e); }} />;
  return <Dashboard token={token} email={email} logout={logout} />;
}
